import { useEffect, useState } from "react";
import type { TelemetryValue } from "../types/telemetry";
import type { GaugeConfigEntry } from "../types/config";
import { GAUGE_STALE_MS, isTelemetryStale } from "../lib/telemetryFreshness";

const CHECK_INTERVAL_MS = 1_000;

/**
 * Compute whether the value for a gauge metric is stale (last update older
 * than the given max age). Pure helper, exported for unit tests.
 */
export function computeAllGaugesStale(
  metrics: Map<string, TelemetryValue>,
  gauges: GaugeConfigEntry[],
  topicPrefix: string,
  now = Date.now(),
  maxAgeMs = GAUGE_STALE_MS,
): boolean {
  let seen = 0;
  for (const gauge of gauges) {
    const key = gauge.topic.replace(topicPrefix, "");
    const telemetry = metrics.get(key);
    if (!telemetry) continue;
    seen++;
    if (!isTelemetryStale(telemetry, now, maxAgeMs)) return false;
  }
  return seen > 0;
}

/**
 * True when every gauge metric that has ever reported a value is now stale.
 * This signals a dead car feed (broker may still be up) so the status bar can
 * warn the operator instead of gauges silently freezing on old values.
 */
export function useTelemetryStaleness(
  metrics: Map<string, TelemetryValue>,
  gauges: GaugeConfigEntry[],
  topicPrefix: string,
): boolean {
  const [allStale, setAllStale] = useState(false);

  useEffect(() => {
    const check = () =>
      setAllStale(computeAllGaugesStale(metrics, gauges, topicPrefix));
    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [metrics, gauges, topicPrefix]);

  return allStale;
}
